import { Tooltip } from '@mui/material';
import { AxiosError } from 'axios';
import React, { createContext, ReactNode, useState } from 'react';

import { axios } from '@bai/data';
import {
  DashboardCourse,
  ErrorHandlerProps,
  getDueSoon,
  getOverdue,
  SectionType,
} from '@bai/utils/types';

import AttemptsExceededLabel from './images/attemptsexceeded-no-outline.svg';
import calendar from './images/Calendar-Due.svg';
import clock from './images/Clock.svg';
import DueSoonLabel from './images/duesoon-no-outline.svg';
import checkmark from './images/Green-Check.svg';
import OverdueLabel from './images/overdue-no-outline.svg';

const careerPathSearchResults = {
  categoryId: 0,
  searchTerm: '',
  selectedLibrary: '',
  filters: [{ name: 'CourseType', values: ['Career Path'] }],
};

const encodedCareerPathSearchToken = btoa(
  JSON.stringify(careerPathSearchResults),
);

export const careerPathPath = `/catalog/search/custom-content?token=${encodedCareerPathSearchToken}`;

const blankRow = <div style={{ height: '1.4em' }} />;

export const iconAndText = (
  id: number,
  icon: string,
  text?: string | number | null,
  textAfter?: string,
  shouldHide?: boolean,
) => {
  let rowName = '';
  switch (icon) {
    case calendar:
      rowName = 'dueDate';
      break;
    case clock:
      rowName = 'duration';
      break;
    case checkmark:
      rowName = 'passingScore';
      break;
    default:
      break;
  }

  if (shouldHide) {
    return blankRow;
  }

  return (
    <div data-cy={`card-${id}-${rowName}`}>
      <img
        alt={rowName}
        data-cy={`card-${id}-${rowName}-icon`}
        src={icon}
        style={
          icon === calendar
            ? {
                height: '1.8em',
                width: 'auto',
                marginLeft: '-0.35em',
                marginRight: '0.5px',
                marginBottom: '-0.2em',
                marginTop: '-0.4em',
              }
            : {
                marginTop: '-0.1em',
                height: '1em',
                width: 'auto',
                marginRight: '0.5em',
              }
        }
      />
      <span data-cy={`card-${id}-${rowName}-text`}>
        {text === null || text === undefined
          ? 'None Specified'
          : `${text}${textAfter || ''}`}
      </span>
    </div>
  );
};

export const cropTextAtLimitAndDisplayTooltip = (
  text: string,
  limit: number,
  style?: React.CSSProperties,
  styleOverrides?: React.HTMLAttributes<HTMLDivElement>,
) =>
  // the + 3 is to prevent truncating with 3 or fewer characters left, only to add the three-character "..."
  text.length > limit + 3 ? (
    <Tooltip
      data-cy="tooltip"
      title={text}
      arrow
      slotProps={{ tooltip: styleOverrides }}
    >
      <span
        data-cy="tooltip_cropped-text"
        style={{
          display: 'inline-block',
          maxWidth: '100%',
          overflowWrap: 'anywhere',
          ...style,
        }}
      >{`${text.substring(0, limit)}...`}</span>
    </Tooltip>
  ) : (
    text
  );

export const getAssignmentStatusLabels = (
  id: number,
  date: string | null,
  section: SectionType,
  retakes: number | null,
  launchButtonStatus: string,
) => (
  <div
    id={`card-${id}-status-labels`}
    data-cy={`card-${id}-status-labels-container`}
    className={`assignment-status-label-container ${section
      .toString()
      .toLowerCase()}`}
  >
    {retakes === 0 && launchButtonStatus.toLowerCase() === 'request' && (
      <img
        className="attemptsexceeded"
        src={AttemptsExceededLabel}
        alt="Assignment Attempts Exceeded Label"
        data-cy="attemptsexceeded-label"
      />
    )}
    {getDueSoon(date) && !getOverdue(date) && (
      <img
        className="duesoon"
        src={DueSoonLabel}
        alt="Assignment Due Soon Label"
        data-cy="duesoon-label"
      />
    )}
    {getOverdue(date) && (
      <img
        className="overdue"
        src={OverdueLabel}
        alt="Assignment Overdue Label"
        data-cy="overdue-label"
      />
    )}
  </div>
);

function caseInsensitiveObjectSort(
  objectArray: DashboardCourse[],
  sortField: 'name' | 'lastScoreDate',
) {
  return objectArray.sort((a, b) => {
    let lowerA;
    let lowerB: string;
    if (sortField === 'name') {
      lowerA = a.name?.toLowerCase();
      lowerB = b.name?.toLowerCase();
    } else {
      lowerA = a.lastScoreDate || '';
      lowerB = b.lastScoreDate || '';
    }
    if (lowerA < lowerB) {
      return -1;
    }
    if (lowerA > lowerB) {
      return 1;
    }
    return 0;
  });
}

interface RetrieveChildCoursesProps {
  id: number;
  setChildCourses: (arg0: DashboardCourse[]) => void;
  setIsLoadingChildCourses: (arg0: boolean) => void;
  // setIsShowingCourseList: (arg0: boolean) => void;
  type: string;
  setErrorMessage: ({
    message,
    messageKey,
    displayForSeconds,
  }: ErrorHandlerProps) => void;
}

export async function retrieveChildCourses(
  retrieveChildCoursesProps: RetrieveChildCoursesProps,
) {
  // event.currentTarget.blur();
  const {
    id,
    setChildCourses,
    setIsLoadingChildCourses,
    setErrorMessage,
    type,
  } = retrieveChildCoursesProps;
  setChildCourses([]);
  setIsLoadingChildCourses(true);
  const response = await axios
    .get<DashboardCourse[]>(
      `api/assignments/coursegroup/${type}/${id}/children`,
    )
    .catch((ex: AxiosError) => {
      setErrorMessage({
        messageKey: `dashboard-section-${type}`,
        message: ex.message,
      });
    })
    .finally(() => {
      setIsLoadingChildCourses(false);
    });
  if (response) {
    if (type?.toLowerCase() === 'careerPath'.toLowerCase()) {
      setChildCourses(response.data);
    } else {
      // course group
      const successfulCourses = response.data.filter(
        (item) => item.status?.toLowerCase() === 'successful',
      );
      const notSuccessfulCourses = response.data.filter(
        (item) => item.status?.toLowerCase() !== 'successful',
      );

      setChildCourses(
        caseInsensitiveObjectSort(notSuccessfulCourses, 'name').concat(
          caseInsensitiveObjectSort(successfulCourses, 'lastScoreDate'),
        ),
      );
    }
  }
}

export function WhiteButton({
  buttonText,
  hoverButtonStyle,
  onClick,
  defaultButtonStyle = {},
  Icon,
}: {
  buttonText: string;
  defaultButtonStyle: React.CSSProperties;
  hoverButtonStyle: React.CSSProperties;
  onClick: () => void;
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
}) {
  const [buttonStyle, setButtonStyle] =
    useState<React.CSSProperties>(defaultButtonStyle);

  const iconStrokeColor = buttonStyle?.color || 'black';

  const [isAnimatingIcon, setIsAnimatingIcon] = useState(false);
  const handleClick = () => {
    if (Icon) {
      setIsAnimatingIcon(true);
      onClick();
      setTimeout(() => {
        setIsAnimatingIcon(false);
      }, 1000);
    } else {
      onClick();
    }
  };

  const defaultIconStyle = {
    fill: iconStrokeColor,
    stroke: iconStrokeColor,
    height: '1.2em',
    marginRight: '0.5em',
    verticalAlign: 'sub',
    width: 'auto',
  };

  const iconStyle = isAnimatingIcon
    ? {
        ...defaultIconStyle,
        transform: 'rotate(360deg)',
        transitionDuration: '1s',
      }
    : defaultIconStyle;

  return (
    <button
      aria-label={buttonText}
      className="basic-button white width-button"
      data-cy={`${buttonText} Button`}
      onClick={handleClick}
      onFocus={() => setButtonStyle(hoverButtonStyle)}
      onBlur={() => setButtonStyle(defaultButtonStyle)}
      onMouseOver={() => setButtonStyle(hoverButtonStyle)}
      onMouseLeave={() => setButtonStyle(defaultButtonStyle)}
      style={buttonStyle}
      type="button"
    >
      {Icon ? (
        <>
          <Icon style={iconStyle} />
          <span>{buttonText}</span>
        </>
      ) : (
        buttonText
      )}
    </button>
  );
}

export interface IDashboardRefreshContext {
  isStudentDashContentRefreshNeeded: boolean;
  setIsStudentDashContentRefreshNeeded: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}

interface DashboardRefreshProviderProps {
  children: ReactNode;
  value: IDashboardRefreshContext;
}

export const DashboardRefreshContext = createContext({
  isStudentDashContentRefreshNeeded: false,

  setIsStudentDashContentRefreshNeeded: (arg: boolean) => {},
});

export function DashboardRefreshProvider({
  children,
  value,
}: DashboardRefreshProviderProps) {
  return (
    <DashboardRefreshContext.Provider value={value}>
      {children}
    </DashboardRefreshContext.Provider>
  );
}
